import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Dot, Kpi, ProgressBar, RagChip, Skel, fmt, pct } from "./shared";

const IndRow = ({ i, onOpen }) => (
  <button type="button" className="w-full grid grid-cols-12 gap-2 items-center px-3 py-2 text-left text-sm hover:bg-slate-50 rounded" onClick={() => onOpen(i)} data-testid={`mon-pv-ind-${i.id}`}>
    <span className="col-span-12 md:col-span-6 truncate">{i.name}</span>
    <span className="col-span-3 md:col-span-1 text-right font-mono text-xs">{fmt(i.target)}</span>
    <span className="col-span-3 md:col-span-2 text-right font-mono text-xs">{fmt(i.actual)}</span>
    <span className="col-span-2 md:col-span-1 text-right font-mono text-xs">{pct(i.pct)}</span>
    <span className="col-span-4 md:col-span-2 text-right"><RagChip i={i} /></span>
  </button>
);

function Outcome({ o, onOpen, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [openOut, setOpenOut] = useState({});
  return (
    <Card data-testid={`mon-outcome-${o.code || o.id}`}><CardContent className="p-0">
      <button type="button" className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setOpen(!open)} data-testid={`mon-outcome-toggle-${o.code || o.id}`}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Dot rag={o.rag} />
        <span className="font-mono text-xs text-slate-500">{o.code}</span>
        <span className="font-medium text-sm flex-1 truncate">{o.title}</span>
        <span className="text-xs text-slate-500">{o.indicator_count} indicators</span>
        <span className="font-mono text-sm w-14 text-right">{o.progress == null ? "—" : pct(o.progress)}</span>
      </button>
      {open && <div className="border-t px-2 py-2 space-y-1">
        {o.indicators.map(i => <IndRow key={i.id} i={i} onOpen={onOpen} />)}
        {o.outputs.map(x => (
          <div key={x.id} className="rounded border border-slate-100">
            <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-50" onClick={() => setOpenOut({ ...openOut, [x.id]: !openOut[x.id] })} data-testid={`mon-output-${x.code || x.id}`}>
              {openOut[x.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}<Dot rag={x.rag} />
              <span className="font-mono text-[11px] text-slate-500">{x.code}</span><span className="text-sm flex-1 truncate">{x.title}</span>
              <span className="text-xs text-slate-500">{x.indicators.length}</span><span className="font-mono text-xs w-12 text-right">{pct(x.progress)}</span>
            </button>
            {openOut[x.id] && <div className="px-1 py-1">{x.indicators.length ? x.indicators.map(i => <IndRow key={i.id} i={i} onOpen={onOpen} />) : <div className="text-xs text-slate-400 px-3 py-2">No indicators on this output.</div>}</div>}
          </div>))}
        {!o.indicators.length && !o.outputs.length && <div className="text-xs text-slate-400 px-3 py-2">No indicators yet.</div>}
      </div>}
    </CardContent></Card>
  );
}

export default function ProjectView({ data, onOpenIndicator }) {
  if (!data) return <><div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4"><Skel rows={1} h="h-16" /></div><Skel rows={5} h="h-12" /></>;
  const c = data.card;
  return (
    <div className="space-y-4" data-testid="mon-project-view">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="mon-kpi-strip">
        <Kpi label="Progress" value={c.progress == null ? "—" : pct(c.progress)} sub={c.progress == null ? (c.targets_set ? "Not started" : "Set targets") : `${c.targets_set}/${c.indicator_count} with targets`} testId="mon-kpi-progress" />
        <Kpi label="Time elapsed" value={pct(c.time_elapsed)} sub={`${c.start_date || "—"} → ${c.end_date || "—"}`} testId="mon-kpi-time" />
        <Kpi label="Budget used" value={pct(c.budget_util)} testId="mon-kpi-budget" />
        <Kpi label="Open advances" value={data.open_advances == null ? "—" : `₹${fmt(data.open_advances)}`} testId="mon-kpi-adv" />
        <Kpi label="Overdue tasks" value={c.overdue_tasks} tone={c.overdue_tasks ? "text-red-700" : ""} testId="mon-kpi-overdue" />
        <Kpi label="Updates due" value={c.missing_updates} tone={c.missing_updates ? "text-amber-700" : ""} testId="mon-kpi-updates" />
      </div>
      <ProgressBar value={c.progress} marker={c.time_elapsed} rag={c.rag} testId="mon-project-bar" />
      <div className="space-y-2">{data.outcomes.length ? data.outcomes.map((o, n) => <Outcome key={o.id} o={o} onOpen={onOpenIndicator} defaultOpen={n === 0} />)
        : <div className="text-sm text-slate-500" data-testid="mon-no-outcomes">No outcomes in this logframe yet.</div>}</div>
    </div>
  );
}
